import { gql } from '@apollo/client';
import type { UUID } from '@cobliteam/common-types';
import { Alert } from '@cobliteam/ui/components';

import type {
  DeviceFragment,
  DeviceIdsQuery,
  DevicesQuery,
  DevicesSpareStatusQuery,
  DriverAssignmentInput,
  LastAssociation,
  UpdateDeviceSpareStatusInput,
} from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';
import type { GraphQLRestDataSourceError } from '../../request/Types';

class GraphQLDeviceRequester {
  public getDevices = async ({
    includeLastMovingStateTime = true,
    includeLastLocation = true,
    includeVehicle = true,
    includeDisablerStatus = false,
    includeTracking = true,
    includeObservationPeriodTrigger = false,
    groupIds,
  }: {
    includeLastMovingStateTime?: boolean;
    includeLastLocation?: boolean;
    includeVehicle?: boolean;
    includeDisablerStatus?: boolean;
    includeTracking?: boolean;
    includeObservationPeriodTrigger?: boolean;
    groupIds?: UUID[];
  }): Promise<DeviceFragment[]> => {
    const queryResult = await graphQLRequester.query<DevicesQuery>({
      variables: {
        includeLastMovingStateTime,
        includeLastLocation,
        includeVehicle,
        includeDisablerStatus,
        includeTracking,
        includeObservationPeriodTrigger,
        groupIds,
      },
      query: gql(`
        fragment Vehicle on VehicleDTO {
          id
          licensePlate
          alias
          size
          model {
            brand
            name
            year
            version
            isSuggestion
            type
          }
          groups {
            id
            name
          }
          driver {
            id
            name
            phoneNumbers
            active
            firstLicenseAcquirementDate
            licenseExpirationDate
            licenseCategory
          }
          operationalSituation {
            id
            situation
            startsAt
            endsAt
            reason
            active
          }
        }

        fragment LastLocation on LastLocation {
          cobliIngestedAt
          timeInMillis
          longitude
          latitude
          speed
          heading
          isPlugged
          ignitionOn
          pluggedUpdateTimestamp
          idleOnDurationInMillis
          idleOffDurationInMillis
        }

        fragment Tracking on DeviceTracking {
          destination {
            address
            lat
            lng
          }
          tempGeofenceId
          conclusionTime
        }

        fragment DeviceActiveObservationPeriodTrigger on Trigger {
          createdAt
        }

        fragment Device on Device {
          id
          cobliId
          fleetId
          type
          generalType
          deviceModel
          capabilities {
            reportsVehicleCan
          }
          healthStatus
          generalStatus
          isNew
          isSpare
          associatedAt
          sdCardHealthStatus
          statusSummary {
            status
            causes {
              cause
              status
            }
          }

          disablerStatus @include(if: $includeDisablerStatus)

          lastMovingStateTime @include(if: $includeLastMovingStateTime)

          tracking @include(if: $includeTracking) {
            ...Tracking
          }

          lastLocation @include(if: $includeLastLocation) {
            ...LastLocation
          }

          vehicle @include(if: $includeVehicle) {
            ...Vehicle
          }

          activeObservationPeriodTrigger @include(if: $includeObservationPeriodTrigger) {
            ...DeviceActiveObservationPeriodTrigger
          }
        }

        query devices(
          $includeLastMovingStateTime: Boolean!,
          $includeLastLocation: Boolean!,
          $includeVehicle: Boolean!,
          $includeDisablerStatus: Boolean!
          $includeTracking: Boolean!
          $includeObservationPeriodTrigger: Boolean!,
          $groupIds: [String],
        ) {
          devices(groupIds: $groupIds) {
            ...Device
          }
        }
    `),
    });

    return queryResult.devices;
  };

  // Para contagens: o fragmento `Device` sempre traz `statusSummary`/`generalStatus`, os resolvers mais lentos do `devices` no janus.
  public getDeviceIds = async (): Promise<string[]> => {
    const queryResult = await graphQLRequester.query<DeviceIdsQuery>({
      query: gql(`
        query DeviceIds {
          devices {
            id
          }
        }
      `),
    });

    return queryResult.devices.map((device) => device.id);
  };

  public getDevicesSpareStatus = async (): Promise<DevicesSpareStatusQuery['devices']> => {
    const queryResult = await graphQLRequester.query<DevicesSpareStatusQuery>({
      query: gql(`
        query DevicesSpareStatus {
          devices {
            id
            isSpare
            vehicle {
              id
            }
          }
        }
      `),
    });

    return queryResult.devices;
  };

  public getDeviceLastAssociation = async (deviceId: string): Promise<LastAssociation> => {
    const queryResult = await graphQLRequester
      .query<{ lastAssociation: LastAssociation }>({
        variables: {
          deviceId,
        },
        query: gql(`
        query lastDeviceAssociation($deviceId: String!) {
          lastAssociation(deviceId: $deviceId) {
            cobliId
            deviceId
            driver {
              driverId
              driverName
            }
            fleetId
            lastLocation {
              deviceId
              time
              timeInMillis
              latitude
              longitude
              heading
              speed
              ignitionOn
              cobliIngestedAt
              processedAt
              isPlugged
              pluggedUpdateTimestamp
              idleOnDurationInMillis
              idleOffDurationInMillis
            }
            vehicle {
              vehicleId
              fleetId
              licensePlate
              alias
              model
              brand
              year
              type
            }
          }
        }

      `),
      })
      .then((result) => result.lastAssociation);

    return queryResult;
  };

  public assignDeviceToDriver = (deviceId: string, driverId: string): Promise<boolean> => {
    const driverAssignmentInput: DriverAssignmentInput = {
      driverId: driverId,
      deviceId: deviceId,
    };
    return graphQLRequester
      .mutate<{
        assignDriverToDevice: boolean;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation AssignDriverToDevice($input: DriverAssignmentInput!) {
            assignDriverToDevice(input: $input)
          }
        `,
        variables: { input: driverAssignmentInput },
      })
      .then((result) => {
        if (result.errors === undefined) {
          Alert.success('Motorista associado com sucesso!');
        } else {
          Alert.error('Erro ao associar o motorista.');
        }

        return result.assignDriverToDevice;
      });
  };

  public updateDeviceSpareStatus = (deviceId: string, isSpare: boolean): Promise<boolean> => {
    const input: UpdateDeviceSpareStatusInput = { deviceId, isSpare };
    return graphQLRequester
      .mutate<{
        updateDeviceSpareStatus: boolean;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation UpdateDeviceSpareStatus($input: UpdateDeviceSpareStatusInput!) {
            updateDeviceSpareStatus(input: $input)
          }
        `,
        variables: { input },
      })
      .then((result) => result.updateDeviceSpareStatus);
  };

  public updateDevicesSpareStatus = (input: UpdateDeviceSpareStatusInput[]): Promise<boolean> => {
    return graphQLRequester
      .mutate<{
        updateDevicesSpareStatus: boolean;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation UpdateDevicesSpareStatus($input: [UpdateDeviceSpareStatusInput!]!) {
            updateDevicesSpareStatus(input: $input)
          }
        `,
        variables: { input },
      })
      .then((result) => result.updateDevicesSpareStatus);
  };

  public unassignDeviceToDriver = (deviceId: string, driverId: string): Promise<boolean> => {
    const driverAssignmentInput: DriverAssignmentInput = {
      driverId: driverId,
      deviceId: deviceId,
    };
    return graphQLRequester
      .mutate<{
        unassignDriverToDevice: boolean;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: gql`
          mutation UnassignDriverToDevice($input: DriverAssignmentInput!) {
            unassignDriverToDevice(input: $input)
          }
        `,
        variables: { input: driverAssignmentInput },
      })
      .then((result) => {
        if (result.errors === undefined) {
          Alert.success('Motorista desassociado com sucesso!');
        } else {
          Alert.error('Erro ao desassociar o motorista.');
        }

        return result.unassignDriverToDevice;
      });
  };
}

export default GraphQLDeviceRequester;
